import { useEffect, useState, useRef } from 'react'
import { useHistoryStore } from '../store/historyStore.js'
import { useUndo } from '../hooks/useUndo.js'

/**
 * UndoToast — bottom-left toast that briefly surfaces after a user mutation
 * so anyone can undo a mistake without knowing the keyboard shortcut.
 *
 * Shows for 6 seconds after each new snapshot, then fades out.
 * Hovering the toast pauses the auto-dismiss timer (Gmail pattern).
 *
 * The ⌘Z keyboard shortcut always works regardless of toast visibility,
 * thanks to useUndo's global listener.
 */
export default function UndoToast() {
  const historyLen = useHistoryStore((s) => s.history.length)
  const lastEntry = useHistoryStore((s) => s.history[s.history.length - 1])
  const { undo, canUndo } = useUndo()
  const [visible, setVisible] = useState(false)
  const [hovering, setHovering] = useState(false)
  const lastShownIdRef = useRef(null)
  const timeoutRef = useRef(null)

  // Show toast whenever a new snapshot is pushed
  useEffect(() => {
    if (!lastEntry) return
    if (lastEntry.id === lastShownIdRef.current) return
    lastShownIdRef.current = lastEntry.id
    setVisible(true)

    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    timeoutRef.current = setTimeout(() => {
      if (!hovering) setVisible(false)
    }, 6000)

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyLen, lastEntry?.id])

  // If user is hovering, don't dismiss
  useEffect(() => {
    if (!hovering && visible && timeoutRef.current === null) {
      timeoutRef.current = setTimeout(() => setVisible(false), 2000)
    }
  }, [hovering, visible])

  if (!visible || !canUndo) return null

  return (
    <div
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      style={{
        position: 'fixed',
        bottom: 20,
        left: 20,
        background: '#111827',
        color: '#f9fafb',
        padding: '10px 14px',
        borderRadius: 10,
        boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
        fontSize: 12,
        fontWeight: 500,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        zIndex: 1000,
        animation: 'slideInLeft 0.2s ease-out',
      }}
    >
      <span style={{ color: '#9ca3af' }}>
        {lastEntry?.label || 'Edited'}
      </span>
      <button
        onClick={() => {
          undo()
          setVisible(false)
        }}
        style={{
          background: 'transparent',
          border: 'none',
          color: '#60a5fa',
          fontSize: 12,
          fontWeight: 700,
          cursor: 'pointer',
          textTransform: 'uppercase',
          letterSpacing: 0.06,
          padding: 0,
        }}
      >
        Undo
      </button>
      <span
        style={{
          color: '#4b5563',
          fontSize: 10,
          fontFamily: 'ui-monospace, monospace',
        }}
      >
        ⌘Z
      </span>
      <button
        onClick={() => setVisible(false)}
        aria-label="Dismiss"
        style={{
          background: 'transparent',
          border: 'none',
          color: '#6b7280',
          fontSize: 14,
          cursor: 'pointer',
          padding: 0,
          marginLeft: 6,
          lineHeight: 1,
        }}
      >
        ×
      </button>
    </div>
  )
}
